import { mkId } from "../utils/coords";

// ── Layer mutations ────────────────────────────────────────────────────────────
export function mutSetLayerVisible(layerId, visible, st) {
  return { ...st, layers: { ...st.layers, [layerId]: { ...st.layers[layerId], visible } } };
}
export function mutSetLayerLocked(layerId, locked, st) {
  return { ...st, layers: { ...st.layers, [layerId]: { ...st.layers[layerId], locked } } };
}
export function mutSetLayerColor(layerId, color, st) {
  return { ...st, layers: { ...st.layers, [layerId]: { ...st.layers[layerId], color } } };
}
export function mutAddLayer(name, color, st) {
  const id = mkId("lyr");
  return { ...st, layers: { ...st.layers, [id]: { id, name, color, visible: true, locked: false, type: "user" } } };
}
export function mutDeleteLayer(layerId, st) {
  if (st.layers[layerId]?.type === "system") return st; // can't delete system layers
  const layers = { ...st.layers }; delete layers[layerId];
  // Orphan symbols/annotations on deleted layer to "annotations" layer
  const symbols     = Object.fromEntries(Object.entries(st.symbols || {}).map(([k,v]) => [k, v.layerId === layerId ? { ...v, layerId: "annotations" } : v]));
  const annotations = Object.fromEntries(Object.entries(st.annotations || {}).map(([k,v]) => [k, v.layerId === layerId ? { ...v, layerId: "annotations" } : v]));
  return { ...st, layers, symbols, annotations };
}

// ── Symbol mutations ───────────────────────────────────────────────────────────
export function mutPlaceSymbol(type, x, y, layerId, rotation = 0, label = "", st) {
  if (st.layers[layerId]?.locked) return st;
  const id = mkId("sym");
  const sym = { id, type, layerId, x: Math.round(x), y: Math.round(y), rotation, label };
  return { ...st, symbols: { ...st.symbols, [id]: sym } };
}
export function mutMoveSymbol(symId, x, y, st) {
  const sym = (st.symbols || {})[symId]; if (!sym) return st;
  if (st.layers[sym.layerId]?.locked) return st;
  return { ...st, symbols: { ...st.symbols, [symId]: { ...sym, x: Math.round(x), y: Math.round(y) } } };
}
export function mutRotateSymbol(symId, rotation, st) {
  const sym = (st.symbols || {})[symId]; if (!sym) return st;
  return { ...st, symbols: { ...st.symbols, [symId]: { ...sym, rotation } } };
}
export function mutDeleteSymbol(symId, st) {
  const syms = { ...(st.symbols || {}) }; delete syms[symId];
  return { ...st, symbols: syms };
}
export function mutLabelSymbol(symId, label, st) {
  const sym = (st.symbols || {})[symId]; if (!sym) return st;
  return { ...st, symbols: { ...st.symbols, [symId]: { ...sym, label } } };
}

// ── Annotation mutations ───────────────────────────────────────────────────────
export function mutAddAnnotation(x, y, text, style = "text", leaderPts = [], st) {
  const id = mkId("ann");
  const ann = { id, layerId: "annotations", x: Math.round(x), y: Math.round(y), text, style, leaderPts };
  return { ...st, annotations: { ...st.annotations, [id]: ann } };
}
export function mutEditAnnotation(annId, text, st) {
  const ann = (st.annotations || {})[annId]; if (!ann) return st;
  return { ...st, annotations: { ...st.annotations, [annId]: { ...ann, text } } };
}
export function mutMoveAnnotation(annId, x, y, st) {
  const ann = (st.annotations || {})[annId]; if (!ann) return st;
  return { ...st, annotations: { ...st.annotations, [annId]: { ...ann, x: Math.round(x), y: Math.round(y) } } };
}
export function mutDeleteAnnotation(annId, st) {
  const anns = { ...(st.annotations || {}) }; delete anns[annId];
  return { ...st, annotations: anns };
}

// ── Manual dimension mutations ─────────────────────────────────────────────────
export function mutAddManualDim(ptA, ptB, offset, text, orientation, st) {
  const id = mkId("dim");
  const dim = { id, layerId: "dimensions", ptA, ptB, offset: offset || 20, text: text || "", orientation: orientation || "h" };
  return { ...st, manualDims: { ...(st.manualDims || {}), [id]: dim } };
}
export function mutDeleteManualDim(dimId, st) {
  const dims = { ...(st.manualDims || {}) }; delete dims[dimId];
  return { ...st, manualDims: dims };
}

// ── Grid line mutations ────────────────────────────────────────────────────────
export function mutAddGridLine(axis, position, label, st) {
  const id = mkId("gl");
  const gl = { id, layerId: "structural", axis, position: Math.round(position), label: label || (axis === "x" ? "A" : "1") };
  return { ...st, gridLines: { ...(st.gridLines || {}), [id]: gl } };
}
export function mutDeleteGridLine(glId, st) {
  const gls = { ...(st.gridLines || {}) }; delete gls[glId];
  return { ...st, gridLines: gls };
}

// ── Drawing settings mutations ─────────────────────────────────────────────────
export function mutUpdateDrawing(fields, st) {
  return { ...st, drawing: { ...(st.drawing || {}), ...fields } };
}
export function mutAddSectionCut(cut, st) {
  const cuts = [...((st.drawing || {}).sectionCuts || []), { id: mkId("sc"), ...cut }];
  return { ...st, drawing: { ...(st.drawing || {}), sectionCuts: cuts } };
}
export function mutDeleteSectionCut(cutId, st) {
  const cuts = ((st.drawing || {}).sectionCuts || []).filter(c => c.id !== cutId);
  return { ...st, drawing: { ...(st.drawing || {}), sectionCuts: cuts } };
}

// ── Extended symbol mutations ─────────────────────────────────────────────────
export function mutScaleSymbol(symId, scale, st) {
  const sym = (st.symbols || {})[symId]; if (!sym) return st;
  return { ...st, symbols: { ...st.symbols, [symId]: { ...sym, scale: Math.max(0.1, scale) } } };
}
export function mutUpdateSymbol(symId, props, st) {
  const sym = (st.symbols || {})[symId]; if (!sym) return st;
  return { ...st, symbols: { ...st.symbols, [symId]: { ...sym, ...props } } };
}
export function mutUpdateAnnotation(annId, props, st) {
  const ann = (st.annotations || {})[annId]; if (!ann) return st;
  return { ...st, annotations: { ...st.annotations, [annId]: { ...ann, ...props } } };
}
export function mutUpdateManualDim(dimId, props, st) {
  const dim = (st.manualDims || {})[dimId]; if (!dim) return st;
  return { ...st, manualDims: { ...st.manualDims, [dimId]: { ...dim, ...props } } };
}
export function mutUpdateGridLine(glId, props, st) {
  const gl = (st.gridLines || {})[glId]; if (!gl) return st;
  return { ...st, gridLines: { ...st.gridLines, [glId]: { ...gl, ...props } } };
}
// ── Multi-select delete ────────────────────────────────────────────────────────
export function mutDeleteMany(selMany, st) {
  let s = st;
  // Delete edges (direct object spread — avoids circular import with mutations.js)
  for (const eId of (selMany.edges || [])) {
    const e = s.edges[eId]; if (!e) continue;
    // Remove edge's half-edges and orphaned nodes
    const newHEs = { ...s.halfEdges };
    delete newHEs[e.halfEdgeA]; delete newHEs[e.halfEdgeB];
    const newEdges = { ...s.edges }; delete newEdges[eId];
    // Remove edge from nodes
    const newNodes = {};
    for (const [nId, n] of Object.entries(s.nodes)) {
      const hids = n.halfEdgeIds.filter(h => h !== e.halfEdgeA && h !== e.halfEdgeB);
      newNodes[nId] = { ...n, halfEdgeIds: hids };
    }
    // Prune orphan nodes
    for (const [nId, n] of Object.entries(newNodes)) {
      if (n.halfEdgeIds.length === 0) delete newNodes[nId];
    }
    s = { ...s, edges: newEdges, halfEdges: newHEs, nodes: newNodes };
  }
  // Delete symbols
  const newSyms = { ...s.symbols };
  for (const sId of (selMany.symbols || [])) delete newSyms[sId];
  // Delete annotations
  const newAnns = { ...s.annotations };
  for (const aId of (selMany.annotations || [])) delete newAnns[aId.startsWith("ann_") ? aId.slice(4) : aId];
  return { ...s, symbols: newSyms, annotations: newAnns };
}
// ── Multi-select move (translate) ─────────────────────────────────────────────
export function mutMoveMany(selMany, dx, dy, st) {
  let s = st;
  // Move nodes (which drags attached walls)
  const newNodes = { ...s.nodes };
  for (const nId of (selMany.nodes || [])) {
    const n = newNodes[nId]; if (!n) continue;
    newNodes[nId] = { ...n, x: Math.round(n.x + dx), y: Math.round(n.y + dy) };
  }
  s = { ...s, nodes: newNodes };
  // Move symbols
  const newSyms = { ...s.symbols };
  for (const sId of (selMany.symbols || [])) {
    const sym = newSyms[sId]; if (!sym) continue;
    newSyms[sId] = { ...sym, x: Math.round(sym.x + dx), y: Math.round(sym.y + dy) };
  }
  // Move annotations
  const newAnns = { ...s.annotations };
  for (const aId of (selMany.annotations || [])) {
    const id = aId.startsWith("ann_") ? aId.slice(4) : aId;
    const ann = newAnns[id]; if (!ann) continue;
    newAnns[id] = { ...ann, x: Math.round(ann.x + dx), y: Math.round(ann.y + dy) };
  }
  return { ...s, symbols: newSyms, annotations: newAnns };
}

// ── Layer rename ───────────────────────────────────────────────────────────────
export function mutRenameLayer(layerId, name, st) {
  if (!st.layers[layerId] || st.layers[layerId].type === "system") return st;
  return { ...st, layers: { ...st.layers, [layerId]: { ...st.layers[layerId], name } } };
}

// ── Layer states (named snapshots) ────────────────────────────────────────────
// Stored as st.layerStates: { [name]: { [layerId]: { visible, locked } } }

export function mutSaveLayerState(name, st) {
  const snapshot = {};
  for (const [id, l] of Object.entries(st.layers || {})) {
    snapshot[id] = { visible: l.visible, locked: l.locked };
  }
  return { ...st, layerStates: { ...(st.layerStates || {}), [name]: snapshot } };
}

export function mutRestoreLayerState(name, st) {
  const snapshot = (st.layerStates || {})[name];
  if (!snapshot) return st;
  const newLayers = { ...st.layers };
  for (const [id, props] of Object.entries(snapshot)) {
    if (newLayers[id]) newLayers[id] = { ...newLayers[id], ...props };
  }
  return { ...st, layers: newLayers };
}

export function mutDeleteLayerState(name, st) {
  const ls = { ...(st.layerStates || {}) };
  delete ls[name];
  return { ...st, layerStates: ls };
}

// ── Angular dimension ─────────────────────────────────────────────────────────
// Measures angle between two edges sharing a node (or any 3 points)
// ptCenter: the vertex, ptA/ptB: points on each arm
export function mutAddAngularDim(ptCenter, ptA, ptB, offset, st) {
  const id = mkId("adim");
  const dim = {
    id, layerId: "dimensions", type: "angular",
    ptCenter: { x: Math.round(ptCenter.x), y: Math.round(ptCenter.y) },
    ptA:      { x: Math.round(ptA.x),      y: Math.round(ptA.y) },
    ptB:      { x: Math.round(ptB.x),      y: Math.round(ptB.y) },
    offset: offset || 800,
    text: "",
  };
  return { ...st, manualDims: { ...(st.manualDims || {}), [id]: dim } };
}

// ── Drawing mode switch ────────────────────────────────────────────────────────
export function mutSetDrawingMode(mode, st) {
  return { ...st, drawingMode: mode };
}
